import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { formatInTimeZone } from 'date-fns-tz';
import './Admin.css';
import ConfirmationModal from '../../components/support/ConfirmationModal';
const API_URL = process.env.REACT_APP_API_URL;
const AdminTranslatorDetailPage = () => {
    const { userId } = useParams();
    const { t, i18n } = useTranslation();
    const { token } = useAuth();
    const location = useLocation();
    
    const [details, setDetails] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isPayoutModalOpen, setIsPayoutModalOpen] = useState(false);

    const fromTab = location.state?.activeTab || 'translators';

    const fetchDetails = useCallback(async () => {
        if (!token) return;
        try {
            setLoading(true); setError('');
            const response = await fetch(`${API_URL}/api/admin/translators/${userId}/full-details`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(t(data.messageKey) || t('error_fetch_translator_details'));
            }
            const data = await response.json();
            setDetails(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    }, [userId, token, t]);

    useEffect(() => {
        fetchDetails();
    }, [fetchDetails]);
    
    const handlePayout = async () => {
        if (!details?.profile?.id) return;
        try {
            const response = await fetch(`${API_URL}/api/admin/translators/${details.profile.id}/payout`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (!response.ok) {
                const data = await response.json();
                throw new Error(t(data.messageKey) || t('error_payout_failed'));
            }
            alert(t('success_payout_processed'));
            fetchDetails(); // Sayfayı en güncel verilerle yenile
        } catch (err) {
            alert(err.message);
        } finally {
            setIsPayoutModalOpen(false);
        }
    };
    
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return formatInTimeZone(new Date(dateString), 'Europe/Istanbul', 'dd.MM.yyyy HH:mm');
    };
    
    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString(i18n.language, {style:'currency', currency:'TRY'});
    };
    
    const totalEarnings = useMemo(() => {
        if (!details?.jobs) return 0;
        return details.jobs
            .filter(job => job.status === 'completed')
            .reduce((sum, job) => sum + parseFloat(job.translator_fee || 0), 0);
    }, [details]);


    if (loading) return <div className="page-container"><i className="fas fa-spinner fa-spin"></i> {t('loading_translator_details')}</div>;
    if (error) return <div className="page-container error">{error}</div>;
    if (!details) return <div className="page-container">{t('translator_not_found')}</div>;

    return (
        <>
           <div className="page-container admin-dashboard narrow-content">
			<div className="detail-page-header-container">
                <Link to="/management/statistics" state={{ activeTab: fromTab }} className="back-link-modern">
                    <i className="fas fa-arrow-left"></i> {t('back_to_statistics')}
                </Link>
                <h1 className="detail-page-title">{details.profile.first_name} {details.profile.last_name}</h1>
                </div>
                <div className="stats-grid">
                    <div className="stat-card"><h3>{t('current_balance')}</h3><p>{formatCurrency(details.profile.balance)}</p></div>
                    <div className="stat-card"><h3>{t('total_earnings_lifetime')}</h3><p>{formatCurrency(totalEarnings)}</p></div>
                    <div className="stat-card"><h3>{t('completed_jobs_count')}</h3><p>{details.jobs.filter(j => j.status === 'completed').length}</p></div>
                </div>

                {/* DÜZELTME BAŞLANGICI */}
                <div className="admin-table-container">
                    <div className="table-header-actions">
                        <h2>{t('payment_history')}</h2>
                        <button 
                            onClick={() => setIsPayoutModalOpen(true)} 
                            className="payout-btn" 
                            disabled={!details.profile.balance || details.profile.balance <= 0}
                        >
                            <i className="fas fa-hand-holding-usd"></i> {t('mark_as_paid_and_reset_balance')}
                        </button>
                    </div>
                    <table className="admin-table">
                        <thead><tr><th>{t('payment_date')}</th><th>{t('amount')}</th><th>{t('processed_by')}</th></tr></thead>
                        <tbody>
                            {details.payments.length > 0 ? details.payments.map(p => (
                                <tr key={p.id}><td>{formatDate(p.payment_date)}</td><td>{formatCurrency(p.amount)}</td><td>Admin #{p.processed_by_admin_id}</td></tr>
                            )) : <tr><td colSpan="3" className="empty-row">{t('no_payments_found')}</td></tr>}
                        </tbody>
                    </table>
                </div>
                {/* DÜZELTME BİTİŞİ */}

                <div className="admin-table-container">
                    <h2>{t('job_history')}</h2>
                    <table className="admin-table">
                        <thead><tr><th>{t('job_id')}</th><th>{t('status')}</th><th>{t('languages')}</th><th>{t('earnings')}</th><th>{t('completed_date')}</th></tr></thead>
                        <tbody>
                            {details.jobs.length > 0 ? details.jobs.map(job => (
                                <tr key={job.id}>
                                    <td><Link to={`/management/jobs/${job.id}`}>#{job.id}</Link></td>
                                    <td><span className={`status-badge status-${job.status.toLowerCase()}`}>{t(`job_status_${job.status}`)}</span></td>
                                    <td>{job.source_lang.toUpperCase()} &rarr; {(Array.isArray(job.target_langs) ? job.target_langs : []).join(', ').toUpperCase()}</td>
                                    <td>{formatCurrency(job.translator_fee)}</td>
                                    <td>{formatDate(job.completed_at)}</td>
                                </tr>
                            )) : <tr><td colSpan="5" className="empty-row">{t('no_jobs_found_for_translator')}</td></tr>}
                        </tbody>
                    </table>
                </div>
            </div>

            <ConfirmationModal
                isOpen={isPayoutModalOpen}
                onClose={() => setIsPayoutModalOpen(false)}
                onConfirm={handlePayout}
                title={t('confirm_payout_title')}
                message={t('confirm_payout_message', { amount: formatCurrency(details.profile.balance), name: `${details.profile.first_name} ${details.profile.last_name}` })}
            />
        </>
    );
  
};

export default AdminTranslatorDetailPage;